import * as React from "react";

import { useAuth } from "@/providers";
import { useConvertRoboqlToSpecMutation } from "@/shared/domain/query/hooks";
import type { ViewRecord } from "@/shared/domain/views";

import { staticFilterFieldsFor } from "../Filters/catalog";
import { fingerprint, hasMovedSince } from "../tableFingerprint";
import type { ResourceView } from "../useResourceView";
import { definitionToViewState } from "../viewSerialization";

export interface ApplyViewOptions {
  /**
   * Whether the host list can show a RoboQL View. Pass `true` from the lists
   * that hand a `roboql` config to their filter bar (datasets, files, events)
   * and nothing from the rest. Without it a RoboQL View is refused with an
   * `error` before any transpile, because applying it would put a filter state
   * on screen that the list's chip row cannot render.
   */
  roboql?: boolean;
}

export interface ApplyView {
  /**
   * Restore a View onto the resource view: filters, sort, page size, columns.
   *
   * Async because a RoboQL View is transpiled first. Never rejects; a failure
   * leaves the view untouched and surfaces on `error`. Resolves `true` when
   * applied — the only signal a caller has, so do not mark a View loaded on
   * `false`.
   *
   * `false` also covers being superseded: a later `applyView` or `supersede`
   * abandons this one, which is not an error and does not set `error`.
   */
  applyView: (record: ViewRecord) => Promise<boolean>;
  /**
   * Abandon whatever apply is in flight, without starting one.
   *
   * For the routes that leave a View by some path other than applying another —
   * clearing, or marking a freshly saved View loaded. Without it a transpile
   * still running would land afterwards and undo them.
   */
  supersede: () => void;
  /**
   * Read the current invalidation generation.
   *
   * Despite living here, this counter is not an apply-only sequence: three of
   * the things that bump it — an org switch, Clear, the emptiness invariant —
   * are not applies at all. It is the resource view's general "in-flight work
   * is no longer wanted" signal, and any operation that resolves after an await
   * can use it the same way `applyView` does: read it before starting, compare
   * before writing, stand down if it moved.
   *
   * That generality is the point. A guard written against one *cause* of
   * invalidation only catches that cause; a guard written against the counter
   * catches every present and future one.
   */
  generation: () => number;
  /** The last failure, or `null`. Cleared when the next apply starts. */
  error: Error | null;
}

/**
 * Turn a chosen View into resource-view state.
 *
 * Separate from `ViewPicker`: applying needs the live view instance and, for
 * RoboQL, a server round trip that the picker has no business making.
 *
 * Filters are applied before sort, so a View's saved sort beats the one
 * `applyFilters` mirrors out of a RoboQL `SORT BY`. A failed transpile applies
 * nothing — clearing filters and then failing would silently widen the result set.
 *
 * A RoboQL View on a list without RoboQL mode is refused the same way, and
 * before the server is asked anything: the backend fences such Views out on
 * write, but rows saved before that fence exist, and a refused apply is a
 * message where a crash was.
 */
export function useApplyView<TData>(
  view: ResourceView<TData>,
  options: ApplyViewOptions = {},
): ApplyView {
  const { roboql: supportsRoboql = false } = options;
  const { currentOrganization } = useAuth();
  const convertRoboql = useConvertRoboqlToSpecMutation();
  const [error, setError] = React.useState<Error | null>(null);
  // How many times in-flight work has been invalidated. A RoboQL View is
  // transpiled before anything is written, so two selections in quick
  // succession can resolve out of order — and the loser must not apply over the
  // winner. `supersede` bumps this for the invalidations that are not applies
  // at all (an org switch, Clear), which is why it is named for the general
  // idea rather than for applies.
  const generationRef = React.useRef(0);

  // Everything a customer can move while a transpile is out, as of the last
  // render. The counter above only knows about applies made through this
  // instance; these are the other ways the view changes under a pending load —
  // the chip bar's Clear calling `clearFilters` directly, `SaveViewControl`'s
  // own applier with its own counter, and the table controls, which change no
  // filters at all.
  //
  // All of them are `useState` in `useResourceView`/`useTable`, so a reference
  // that has not moved means the customer has not touched it. Held in a ref so
  // the running apply compares against the latest render rather than the one
  // it closed over.
  const liveRef = React.useRef(fingerprint(view));
  // A layout effect rather than a passive one, matching `useLoadedView`: this
  // is read after an await, so a fingerprint that lagged the commit would let a
  // landing transpile conclude the customer had not moved the view when they
  // just had, and overwrite their change. Updating in the commit means any
  // continuation sees what is actually on screen.
  React.useLayoutEffect(() => {
    liveRef.current = fingerprint(view);
  });

  const { applyFilters, table } = view;
  const { setSort, setPageSize, setColumnOrder, setColumnVisibility } = table;
  const convertAsync = convertRoboql.mutateAsync;

  const applyView = React.useCallback(
    async (record: ViewRecord): Promise<boolean> => {
      const seq = ++generationRef.current;
      const stateAtStart = liveRef.current;
      setError(null);

      let restored;
      try {
        restored = definitionToViewState(
          record.definition,
          staticFilterFieldsFor(record.target),
        );
      } catch (caught) {
        // Written under a newer scheme; there is nothing to apply.
        setError(asError(caught));
        return false;
      }

      if (restored.filters.type === "roboql") {
        if (!supportsRoboql) {
          setError(
            new Error(
              `"${record.name}" was written in RoboQL, which the ${record.target} ` +
                "table cannot show. Re-save it with filters, or delete it.",
            ),
          );
          return false;
        }
        let roboqlFilters;
        try {
          const spec = await convertAsync({
            query: restored.filters.query.toString(),
            orgId: currentOrganization?.org_id,
          });
          roboqlFilters = {
            type: "roboql" as const,
            query: restored.filters.query,
            spec,
          };
        } catch (caught) {
          // Only the current apply owns `error`; a superseded one failing is
          // not something the customer needs told about.
          if (seq === generationRef.current) {
            setError(asError(caught));
          }
          return false;
        }
        // Re-checked after the await, against both halves: a newer apply
        // through this hook, or the customer having moved the view themselves.
        // Their change is the more recent statement of intent, so the View
        // stands down rather than landing on top of it.
        if (
          seq !== generationRef.current ||
          hasMovedSince(stateAtStart, liveRef.current)
        ) {
          return false;
        }
        applyFilters(roboqlFilters);
      } else {
        applyFilters(restored.filters);
      }

      // After the filters — see the note above on ordering.
      if (restored.sort !== null) {
        setSort(restored.sort);
      }
      if (restored.pageSize !== null) {
        setPageSize(restored.pageSize);
      }
      if (restored.visibleColumns !== null) {
        applyColumns(restored.visibleColumns, table.visibleColumnIds, {
          setColumnOrder,
          setColumnVisibility,
        });
      }

      return true;
    },
    [
      applyFilters,
      convertAsync,
      currentOrganization?.org_id,
      setColumnOrder,
      setColumnVisibility,
      setPageSize,
      setSort,
      supportsRoboql,
      table.visibleColumnIds,
    ],
  );

  const supersede = React.useCallback(() => {
    generationRef.current += 1;
  }, []);

  // A function rather than the value, so a caller reading it after an await
  // sees the counter as it is now rather than as it was when they rendered.
  const generation = React.useCallback(() => generationRef.current, []);

  return { applyView, supersede, generation, error };
}

/**
 * Reconcile the table's columns to what the View saved. Columns the View does
 * not mention are hidden — the saved list is a complete statement, not an
 * addition to the current set.
 */
function applyColumns(
  savedColumns: string[],
  currentlyVisible: ReadonlySet<string>,
  setters: {
    setColumnOrder: (order: string[]) => void;
    setColumnVisibility: (id: string, visible: boolean) => void;
  },
): void {
  const saved = new Set(savedColumns);

  for (const id of savedColumns) {
    if (!currentlyVisible.has(id)) {
      setters.setColumnVisibility(id, true);
    }
  }
  for (const id of currentlyVisible) {
    if (!saved.has(id)) {
      setters.setColumnVisibility(id, false);
    }
  }

  setters.setColumnOrder(savedColumns);
}

function asError(caught: unknown): Error {
  return caught instanceof Error ? caught : new Error(String(caught));
}
